import { Button } from "@/components/ui/button/button";
import { Card } from "@/components/ui/card/card";
import { ErrorPopover } from "@/components/ui/error-popover/error-popover";
import { QueryError } from "@/components/ui/query-error/query-error";
import { TaskStatus } from "@/components/ui/task-status/task-status";
import { cn } from "@/lib/utils";
import { useEquimentConfiguration } from "@/queries/configuration/use-equiment-configuration";
import { useProductsWaitingForValidation } from "@/queries/tasks/use-products-waiting-for-validation";
import { Task, TaskTypes } from "@/queries/tasks/use-tasks";
import { Barcode } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type TranslationKeys = "packagingPrinting" | "engrave" | "marking";

interface MarkingTaskSharedProps {
  task: Task;
  autoMark: boolean;
  startedManually: boolean;
  isPending: boolean;
  onMark: VoidFunction;
  error: Error | null;
  textSlot?: ReactNode;
}

export function MarkingTaskShared({
  task,
  autoMark,
  startedManually,
  isPending,
  onMark,
  error,
  textSlot,
}: MarkingTaskSharedProps) {
  const { t } = useTranslation();
  const { config } = useEquimentConfiguration("ValidationSkip");
  const {
    productsWaitingForValidation,
    error: waitingValidationError,
    refetch: refectWaitingValidation,
  } = useProductsWaitingForValidation();

  const skipValidation = config?.PropertyValue?.toLowerCase() === "true";

  const hasWaitingProducts = !!productsWaitingForValidation?.length;

  const markerStarted = autoMark || startedManually;
  const operatorShouldStartMarker = !markerStarted && !hasWaitingProducts;

  const awaitingMarker = markerStarted && !hasWaitingProducts;
  const awaitingValidation = hasWaitingProducts && !skipValidation;

  const [validatedProduct] =
    productsWaitingForValidation ?? ([] as undefined[]);

  const validationPassed =
    validatedProduct?.ProductId === validatedProduct?.ValidatedProductId;

  const types: Record<TaskTypes, TranslationKeys> = {
    GF_PackingTaskDefinition: "packagingPrinting",
    GF_EngraverTaskDefinition: "engrave",
    GF_MarkingTaskDefinition: "marking",
  };

  const type = types[task?.TaskDefinitionNId];

  if (waitingValidationError) {
    return (
      <QueryError
        resource={t("task.productsWaitingForValidation")}
        errors={[waitingValidationError]}
        retries={[refectWaitingValidation]}
      />
    );
  }

  return (
    <>
      <Card className={cn("p-4 shadow-task-sm")}>
        <p className="flex gap-2 text-xl font-bold items-center">
          <Barcode className="h-6 w-6" />
          {task?.Description}
        </p>

        <p className="text-lg">
          {awaitingValidation && t(`task.${type}.waitingForValidationText`)}

          {awaitingMarker &&
            !awaitingValidation &&
            t(`task.${type}.waitingForTaskText`)}

          {!markerStarted &&
            !awaitingValidation &&
            t(`task.${type}.manualStart`)}

          {textSlot}
        </p>
      </Card>

      {(awaitingMarker || awaitingValidation) && (
        <TaskStatus
          isLoading={!validatedProduct?.ValidatedProductId}
          status={
            !validatedProduct?.ValidatedProductId
              ? "default"
              : validationPassed
                ? "success"
                : "error"
          }
        >
          <p className="text-lg font-semibold">
            {!validatedProduct?.ValidatedProductId && (
              <>
                {awaitingMarker && t(`task.${type}.waitingForTask`)}

                {awaitingValidation && t(`task.${type}.waitingForValidation`)}
              </>
            )}
            {validatedProduct?.ValidatedProductId}
          </p>
        </TaskStatus>
      )}

      <ErrorPopover
        error={error}
        className="max-w-[var(--radix-popover-trigger-width)]"
      >
        <Button
          size="default"
          variant={operatorShouldStartMarker ? "default" : "secondary"}
          onClick={onMark}
          loading={isPending}
          disabled={isPending}
          className={cn(
            "shadow-task-sm",
            !operatorShouldStartMarker && "border ring-0"
          )}
        >
          {operatorShouldStartMarker
            ? t(`task.${type}.button`)
            : t("task.buttonRetry")}
        </Button>
      </ErrorPopover>
    </>
  );
}
